import { apiClient } from "@/services/api";
import { logError } from "@/lib/error-handling";
import { CompetitorDto } from "@/types";

export interface CompetitorApiData {
  name: string;
  website?: string | null;
}

/**
 * Creates a new competitor by sending the provided data to the backend.
 * Admin-only endpoint.
 *
 * @param data - The data required to create a new competitor
 * @returns A promise that resolves with the response data from the backend
 * @throws Will throw an error if the request fails or if validation fails
 */
export async function createCompetitor(
  data: CompetitorApiData
): Promise<CompetitorDto> {
  try {
    return await apiClient<CompetitorDto>(`/api/admin/competitors`, {
      method: "POST",
      body: JSON.stringify(data),
    });
  } catch (error) {
    logError(error, "createCompetitor");
    throw error;
  }
}

/**
 * Fetches all competitors for the current organization.
 * Admin-only endpoint.
 *
 * @returns A list of competitors
 * @throws An error if the request fails
 */
export async function fetchCompetitors(): Promise<CompetitorDto[]> {
  try {
    return await apiClient<CompetitorDto[]>(`/api/admin/competitors`, {
      method: "GET",
      cache: "no-store",
    });
  } catch (error) {
    logError(error, "fetchCompetitors");
    throw error;
  }
}

/**
 * Fetches a single competitor by its ID.
 * Admin-only endpoint.
 *
 * @param competitorId - The ID of the competitor to fetch
 * @returns The competitor data
 * @throws An error if the request fails
 */
export async function fetchCompetitorById(
  competitorId: string
): Promise<CompetitorDto> {
  try {
    return await apiClient<CompetitorDto>(
      `/api/admin/competitors/${competitorId}`,
      {
        method: "GET",
        cache: "no-store",
      }
    );
  } catch (error) {
    logError(error, "fetchCompetitorById");
    throw error;
  }
}

/**
 * Updates a competitor by sending the provided data to the backend.
 * Admin-only endpoint.
 *
 * @param competitorId - The ID of the competitor to update
 * @param data - The data required to update the competitor
 * @returns A promise that resolves with the response data from the backend
 * @throws Will throw an error if the request fails or if validation fails
 */
export async function updateCompetitor(
  competitorId: string,
  data: CompetitorApiData
): Promise<CompetitorDto> {
  try {
    return await apiClient<CompetitorDto>(
      `/api/admin/competitors/${competitorId}`,
      {
        method: "PATCH",
        body: JSON.stringify(data),
      }
    );
  } catch (error) {
    logError(error, "updateCompetitor");
    throw error;
  }
}

/**
 * Deletes a competitor by its ID.
 * Admin-only endpoint.
 *
 * @param competitorId - The ID of the competitor to delete
 * @returns A promise that resolves when the competitor is deleted
 * @throws An error if the request fails
 */
export async function deleteCompetitor(competitorId: string): Promise<void> {
  try {
    await apiClient<void>(`/api/admin/competitors/${competitorId}`, {
      method: "DELETE",
    });
  } catch (error) {
    logError(error, "deleteCompetitor");
    throw error;
  }
}
